import pluralize from "pluralize";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { exists } from "@examspark/common";
import {
  fromAWSDate,
  getFullAcademicCalendarFromCourseCalendar,
  nonInstructionDatesByDate,
  toAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  NoResults,
  useQueryGetClassroomCourseCalendar,
  useQueryGetCourseUnits,
  useQueryListPlannerItems,
} from "@examspark/common-app";
import {
  inputGlow,
  Calendar,
  Grid,
  Spinner,
  Tooltip,
} from "@examspark/common-ui";
import { IconWarningTriangleLine } from "@examspark/common-ui/dist/components/icons";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";
import { Classroom, CourseCalendar, Unit, UnitPlan } from "@examspark/types";

import SetupPrompt from "../../common/SetupPrompt";
import PlannerDayViewModal from "../../dashboard/PlannerDayViewModal";
import { useMemoGetActiveMonths } from "../../workspace/WorkspaceCalendar";

const colorWheel = [
  "hsl(300, 50%, 85%)",
  "hsl(20, 50%, 85%)",
  "hsl(45, 50%, 85%)",
  "hsl(60, 50%, 85%)",
  "hsl(90, 50%, 85%)",
  "hsl(150, 50%, 85%)",
  "hsl(180, 50%, 85%)",
  "hsl(210, 50%, 85%)",
  "hsl(240, 50%, 85%)",
  "hsl(270, 50%, 85%)",
  "hsl(330, 50%, 85%)",
  "hsl(360, 50%, 85%)",
  "hsl(0, 50%, 85%)",
  "hsl(30, 50%, 85%)",
];

const instructionDays = [1, 2, 3, 4, 5];

export const StyledCalendar = styled.div`
  --radius: 2rem;

  .react-calendar__month-view {
    /* max-width: 25rem; */
    margin: 0 auto;
  }

  .react-calendar {
    border: none;
    font-family: inherit;
    background-color: transparent;
  }
  .react-calendar__tile {
    color: #222;
    font-size: 1.2rem;
    /* overflow: visible !important; */
  }
  .react-calendar__month-view__days__day {
    aspect-ratio: 1;
  }
  .react-calendar__navigation button:disabled {
    background: none;
    transition: all 0.2s;
    position: relative;
  }
  .react-calendar__tile--active {
    color: #222;
  }
  .react-calendar__month-view__days__day--neighboringMonth {
    /* color: #ccc; */
    opacity: 0.4;
  }
  .react-calendar__month-view__weekdays__weekday {
    color: #333;
  }
  .react-calendar__tile--active {
    background-color: transparent;
  }
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile:enabled:hover {
    background-color: ${colorFromTheme("surface3")};
  }
  .react-calendar__month-view__days__day--weekend {
    color: #aaa;
  }
  .react-calendar__month-view__days {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem 0;
  }
  .react-calendar__month-view__days__day {
    position: relative;
  }
  .react-calendar__tile--now {
    background-color: transparent;
    text-decoration: underline;
    font-weight: bold;
  }
  .react-calendar__tile--now:enabled:hover {
    background-color: ${colorFromTheme("surface3")};
  }

  .react-calendar-unit-start {
    border-radius: var(--radius) 0 0 var(--radius);
    overflow: visible !important;
  }
  .react-calendar-unit-end {
    border-radius: 0 var(--radius) var(--radius) 0;
    overflow: visible !important;
  }

  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }
  .react-calendar-grading-period-start {
    &:before {
      content: "[";
    }
  }
  .react-calendar-grading-period-end {
    &:after {
      content: "]";
    }
  }

  .react-calendar-holiday {
    border-radius: 0.5rem;
    background-color: #ddd;
    color: #666;
  }
  .react-calendar-holiday_HOLIDAY {
    background-color: #d7b547;
    color: #fff;
  }
  .react-calendar-holiday_TEACHER_WORKDAY {
    background-color: #aaa;
    color: #fff;
  }
  .react-calendar-holiday_OTHER {
    background-color: #035e7b;
    color: #fff;
  }
  .react-calendar-holiday_ASSESSMENT {
    background-color: #e44e4e;
    color: #fff;
  }
  .react-calendar-holiday abbr {
    position: relative;
  }

  .react-calendar__tile:focus-visible,
  .react-calendar__tile:focus-within {
    ${inputGlow}
    z-index: 1;
  }

  ${colorWheel.map(
    (color, i) => `
    & .react-calendar-unit-color-${i} {
      background: ${color};
    }
    & .react-calendar-unit-color-${i}.react-calendar__tile:enabled:focus {
      background: ${color};
    }

  `,
  )}

  .react-calendar__tile:disabled {
    background: none;
  }
`;

const BracketStart = styled.div`
  position: absolute;
  text-align: center;
  left: calc(50% - 13px);
  top: calc(50% - 6px);

  svg {
    fill: #444;
  }
`;
const BracketEnd = styled.div`
  position: absolute;
  text-align: center;
  right: calc(50% - 13px);
  top: calc(50% - 6px);

  svg {
    fill: #444;
  }
`;

const UnitPill = styled.div`
  position: absolute;
  padding: 0px 4px;
  min-width: 1.5rem;
  border-radius: 1rem;
  font-family: var(--font-family-sans-serif);
  font-size: 1rem;
  text-align: center;

  background-color: #5a5a5a;
  color: white;

  top: -4px;
  left: 0;
`;

const Month = styled.div`
  padding: 1rem;
  background-color: ${colorFromTheme("surface1")};
  border-radius: 0.5rem;
  border: solid 1px #f0f0f0;
`;

const MonthName = styled.h1`
  font-weight: 300;
  font-size: 2rem;
  color: salmon;
  margin-bottom: 1rem;
  padding: 0 0.8rem;
`;

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const StyledTooltip = styled.div`
  /* width: 15rem; */
`;

const SelectedCheckmark = styled.div`
  position: absolute;
  bottom: 0rem;
  right: 0rem;

  // selected experimentally based on the modal at different sizes
  width: 35%;
  height: 45%;

  svg {
    fill: rebeccapurple;
  }
`;

const ScheduledBadge = styled.div`
  position: absolute;
  width: 0.8rem;
  height: 0.8rem;
  bottom: 0.4rem;
  right: 0.4rem;
  border-radius: 100%;
  background-color: rebeccapurple;
`;

type MonthGridProps = {
  monthDates: Date[];
  selectedDates?: Date[];
  courseCalendar: Pick<
    CourseCalendar,
    "units" | "calendar" | "course" | "additionalNonInstructionDates"
  >;
  onClickDay?: (value: Date) => void;

  onlyShowPinsFor?: {
    resource?: string;
    classroom?: string;
  };
  slim?: boolean;
};

export const MonthGrid = ({
  monthDates,
  selectedDates,
  courseCalendar,
  onlyShowPinsFor,
  onClickDay,
}: MonthGridProps) => (
  <Grid size="24rem" gap="2rem">
    {monthDates.map((month) => (
      <Month key={month.toISOString()}>
        <MonthName>
          {months[month.getMonth()]}
          {" '"}
          {month.getFullYear().toString().substring(2)}
        </MonthName>
        <ClassroomCalendarMonth
          courseCalendar={courseCalendar}
          month={month}
          showNavigation={false}
          onlyShowPinsFor={onlyShowPinsFor}
          onClickDay={onClickDay}
          selectedDates={selectedDates}
        />
      </Month>
    ))}
  </Grid>
);

export const ClassroomCalendarMonth = ({
  courseCalendar,
  month,
  showNavigation,
  onMonthChange,
  onClickDay,
  onlyShowPinsFor,
  selectedDates,
}: {
  month: Date;
  courseCalendar: Pick<
    CourseCalendar,
    "units" | "calendar" | "course" | "additionalNonInstructionDates"
  >;
  showNavigation?: boolean;
  onlyShowPinsFor?: {
    resource?: string;
    classroom?: string;
  };
  onMonthChange?: (month: Date) => void;
  onClickDay?: (date: Date) => void;
  selectedDates?: Date[];
}) => {
  const [popupRef, setPopupRef] = useState<any>();

  const courseWithUnitsQuery = useQueryGetCourseUnits(
    courseCalendar.course?.id,
  );

  const units = courseCalendar.units;
  const calendar = getFullAcademicCalendarFromCourseCalendar(courseCalendar);

  // get a datestring in the form of 2024-01 for the month to sk.beginsWith inside
  const monthString = toAWSDate(month).slice(0, 7);
  const scheduledDatesQuery = useQueryListPlannerItems(monthString);

  const scheduledDates = useMemo(
    () =>
      scheduledDatesQuery.data
        ?.filter(
          (item) =>
            onlyShowPinsFor == null ||
            (item.resourceId === onlyShowPinsFor.resource &&
              item.id === onlyShowPinsFor.classroom),
        )
        ?.map((item) => item.sk.split(":").at(1))
        .filter(exists)
        .map(fromAWSDate) ?? [],
    [scheduledDatesQuery.data, onlyShowPinsFor],
  );

  // if (!calendar) return null;

  const gradingPeriodStartDates = new Set(
    calendar
      ? calendar.gradingPeriods?.map((gradingPeriod) => gradingPeriod.startAt)
      : [],
  );
  const gradingPeriodEndDates = new Set(
    calendar
      ? calendar.gradingPeriods?.map((gradingPeriod) => gradingPeriod.endAt)
      : [],
  );

  const plannedUnitsById = units?.reduce(
    (acc, unit) => {
      if (!unit.id) return acc;
      acc[unit.id] = {
        ...unit,
        unit: courseWithUnitsQuery.data?.find((u) => u.id === unit.id),
      };
      return acc;
    },
    {} as Record<string, UnitPlan & { unit: Unit | undefined }>,
  );

  const popupMonth = `${month.getFullYear()}-${month.getMonth() + 1}`;
  const popupMonthPortalId = `calendar-${popupMonth}-popup-portal`;

  const nonInstructionDatesNew = calendar
    ? nonInstructionDatesByDate(calendar.nonInstructionDates)
    : {};

  return (
    <>
      <div
        id={popupMonthPortalId}
        ref={(node) => {
          setPopupRef(node);
        }}
      ></div>
      <Calendar
        locale="en-US"
        onActiveStartDateChange={({ activeStartDate }) =>
          // @ts-expect-error bad param
          onMonthChange?.(activeStartDate)
        }
        value={month}
        showNavigation={showNavigation}
        showNeighboringMonth={false}
        maxDetail="month"
        minDetail="month"
        onClickDay={onClickDay}
        tileContent={({ date }) => {
          const annotations: string[] = [];
          if (date.getMonth() !== month.getMonth()) return null;
          if (!plannedUnitsById) return null;

          const unitPlanForDate = units?.find((unit) =>
            unit.dates?.find((d) => d === toAWSDate(date)),
          );
          const unit = plannedUnitsById[unitPlanForDate?.id ?? ""];
          const unitDayIndex =
            unit?.dates?.findIndex((d) => d === toAWSDate(date)) ?? -1;
          const unitIndex = units?.findIndex((cu) => cu.id === unit?.id);

          const elements: any[] = [];

          const nid = nonInstructionDatesNew?.[toAWSDate(date)];
          if (nid?.label) {
            annotations.push(nid.label);
          }

          if (gradingPeriodStartDates.has(toAWSDate(date))) {
            elements.push(
              <BracketStart>
                <svg
                  version="1.1"
                  id="Layer_1"
                  x="0px"
                  y="0px"
                  viewBox="0 0 5 12"
                  height="12"
                >
                  <polygon points="5,12 5,10 2,10 2,2 5,2 5,0 0,0 0,12 " />
                </svg>
              </BracketStart>,
            );
            annotations.push(
              `${
                calendar?.gradingPeriods?.find(
                  (gp) => gp.startAt === toAWSDate(date),
                )?.label ?? "Grading Period"
              } begins`,
            );
          }
          if (gradingPeriodEndDates.has(toAWSDate(date))) {
            elements.push(
              <BracketEnd>
                <svg
                  version="1.1"
                  id="Layer_1"
                  x="0px"
                  y="0px"
                  viewBox="0 0 5 12"
                  height="12"
                >
                  <polygon points="0,0 0,2 3,2 3,10 0,10 0,12 5,12 5,0 " />
                </svg>
              </BracketEnd>,
            );
            annotations.push(
              `${
                calendar?.gradingPeriods?.find(
                  (gp) => gp.endAt === toAWSDate(date),
                )?.label ?? "Grading Period"
              } ends`,
            );
          }

          if (unitDayIndex === 0) {
            elements.push(
              <UnitPill>
                {unit?.unit?.numberLabel ??
                  (unitIndex == null ? undefined : (unitIndex + 1).toString())}
              </UnitPill>,
            );
          }
          if (unitIndex != null && unit) {
            annotations.push(
              unit.unit?.name ??
                (unit.unit?.numberLabel
                  ? `Unit / Module ${unit?.unit?.numberLabel}`
                  : `Unit / Module ${unitIndex + 1}`),
            );
          }

          if (
            scheduledDates?.find((d) => {
              return toAWSDate(d) === toAWSDate(date);
            })
          ) {
            elements.push(<ScheduledBadge />);
            const plannerItemCount = scheduledDates?.filter(
              (d) => toAWSDate(d) === toAWSDate(date),
            ).length;
            annotations.push(
              `${plannerItemCount} ${pluralize(
                "item",
                plannerItemCount,
              )} pinned`,
            );
          }

          if (
            selectedDates?.find((d) => d.toISOString() === date.toISOString())
          ) {
            elements.push(
              <SelectedCheckmark>
                <svg x="0px" y="0px" viewBox="0 0 408.576 408.576">
                  <g>
                    <g>
                      <path d="M204.288,0C91.648,0,0,91.648,0,204.288s91.648,204.288,204.288,204.288s204.288-91.648,204.288-204.288    S316.928,0,204.288,0z M318.464,150.528l-130.56,129.536c-7.68,7.68-19.968,8.192-28.16,0.512L90.624,217.6    c-8.192-7.68-8.704-20.48-1.536-28.672c7.68-8.192,20.48-8.704,28.672-1.024l54.784,50.176L289.28,121.344    c8.192-8.192,20.992-8.192,29.184,0C326.656,129.536,326.656,142.336,318.464,150.528z"></path>
                    </g>
                  </g>
                </svg>
              </SelectedCheckmark>,
            );
          }

          if (annotations.length) {
            elements.push(
              <Tooltip
                placement="top"
                tip={
                  <StyledTooltip>
                    {annotations.map((a, idx) => (
                      <div key={idx}>{a}</div>
                    ))}
                  </StyledTooltip>
                }
                portalTarget={popupRef}
              >
                {/* create a div that covers the entire day cell to attach the onHover events to */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Tooltip>,
            );
          }

          return (
            <>
              {elements.map((el, i) => (
                <React.Fragment key={i}>{el}</React.Fragment>
              ))}
            </>
          );
        }}
        showFixedNumberOfWeeks
        tileDisabled={({ date }) => {
          if (date.getMonth() !== month.getMonth()) return true;
          return false;
        }}
        tileClassName={({ date }) => {
          // Filter out days not in day-of-week
          const day = date.getDay();
          if (!instructionDays.includes(day)) return "";
          // filter out days not in current month
          if (date.getMonth() !== month.getMonth()) return "";

          if (nonInstructionDatesNew?.[toAWSDate(date)]?.type) {
            return `react-calendar-holiday react-calendar-holiday_${
              nonInstructionDatesNew?.[toAWSDate(date)]?.type
            }`;
          }

          if (!units) return "";

          // filter out days that have no unit (and are not a holiday)
          const unitPlan = units.find((unit) =>
            unit.dates?.find((d) => d === toAWSDate(date)),
          );
          if (!unitPlan?.id || !plannedUnitsById) return "";
          const unit = plannedUnitsById[unitPlan.id];
          const unitDayIndex =
            unit?.dates?.findIndex((d) => d === toAWSDate(date)) ?? -1;

          if (!unit) return "";

          // style unit days
          const classes: string[] = [
            `react-calendar-unit-color-${
              units.findIndex((u) => u.id === unit.id) % colorWheel.length
            }`,
            `react-calendar-unit-${units.findIndex((u) => u.id === unit.id)}`,
          ];
          if (unit.dates && unitDayIndex === 0) {
            classes.push("react-calendar-unit-start");
          }
          if (unit.dates && unitDayIndex === unit.dates.length - 1) {
            classes.push("react-calendar-unit-end");
          }
          return classes.join(" ");
        }}
      />
    </>
  );
};

export const ClassroomPlannerPicker = ({
  courseCalendar,
  onDayClick,
  selectedDates,
  onlyShowPinsFor,
}: {
  courseCalendar: Pick<
    CourseCalendar,
    "units" | "calendar" | "course" | "additionalNonInstructionDates"
  >;
  onDayClick?: (date: Date) => void;
  selectedDates?: Date[];
  onlyShowPinsFor?: {
    resource?: string;
    classroom?: string;
  };
}) => {
  const defaultMonths = useMemoGetActiveMonths(
    courseCalendar.calendar?.startAt
      ? fromAWSDate(courseCalendar.calendar?.startAt)
      : undefined,
  );
  const [monthDates, setMonthDates] = useState<Date[] | null>();

  useEffect(() => {
    if (monthDates == null) {
      setMonthDates(defaultMonths);
    }
  }, [monthDates, defaultMonths]);

  return (
    <StyledCalendar>
      {courseCalendar.units && courseCalendar.calendar && (
        <MonthGrid
          courseCalendar={courseCalendar}
          monthDates={defaultMonths}
          selectedDates={selectedDates}
          onClickDay={onDayClick}
          onlyShowPinsFor={onlyShowPinsFor}
          slim
        />
      )}
    </StyledCalendar>
  );
};

const ClassroomPlannerCalendar = ({ classroom }: { classroom: Classroom }) => {
  const courseCalendarQuery = useQueryGetClassroomCourseCalendar(classroom);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();

  const monthDates = useMemoGetActiveMonths(
    courseCalendarQuery.data?.calendar?.startAt
      ? fromAWSDate(courseCalendarQuery.data.calendar.startAt)
      : undefined,
  );

  if (courseCalendarQuery.isError) {
    return (
      <NoResults icon={<IconWarningTriangleLine width={16} />}>
        Error Loading Course Calendar
      </NoResults>
    );
  }

  if (courseCalendarQuery.isLoading) {
    return <NoResults icon={<Spinner />}>Loading Course Calendar</NoResults>;
  }

  if (courseCalendarQuery.isSuccess && !courseCalendarQuery.data) {
    return (
      <SetupPrompt>
        <SetupPrompt.Title>No Calendar Selected</SetupPrompt.Title>
        <SetupPrompt.Text>
          Please select a calendar in Classroom Settings.
        </SetupPrompt.Text>
      </SetupPrompt>
    );
  }

  return (
    <>
      <PlannerDayViewModal
        isOpen={isDayModalOpen}
        onRequestClose={() => setIsDayModalOpen(false)}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
      <StyledCalendar>
        {courseCalendarQuery.data?.units &&
          courseCalendarQuery.data?.calendar && (
            <MonthGrid
              courseCalendar={courseCalendarQuery.data}
              monthDates={monthDates}
              onClickDay={(value) => {
                setIsDayModalOpen(true);
                setSelectedDate(value);
              }}
            />
          )}
      </StyledCalendar>
    </>
  );
};

export default ClassroomPlannerCalendar;
